var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useGeneratedId } from 'modules/utils';
import { useAutoHeight } from 'modules/v2/utils/useAutoHeight';
import { useDescriptionWarning } from './hooks';
import { removeLineBreaks } from './utils';
import { Label } from '../Label';
import { Container, StyledTextArea, Description, TextAreaContainer, Adornment } from './styled';
export const TextArea = forwardRef((props, forwardedRef) => {
    const autoId = useGeneratedId('textinput');
    const { variant = 'outline', id = autoId, label, 'aria-describedby': ariaDescribedBy, description, placeholder, stretch = false, invalid = false, disabled = false, appearance, adornmentLeft, adornmentRight, defaultValue, value, onChange, resize = 'none', preventLinebreaks = false, autosizeHeight = false, minHeight = 16, maxHeight, labelSize = 'small' } = props, restProps = __rest(props, ["variant", "id", "label", 'aria-describedby', "description", "placeholder", "stretch", "invalid", "disabled", "appearance", "adornmentLeft", "adornmentRight", "defaultValue", "value", "onChange", "resize", "preventLinebreaks", "autosizeHeight", "minHeight", "maxHeight", "labelSize"]);
    const [unControlledValue, setUncontrolledValue] = useState(defaultValue);
    const textAreaRef = useRef(null);
    useImperativeHandle(forwardedRef, () => textAreaRef.current);
    useAutoHeight({
        textAreaRef,
        textAreaValue: value || unControlledValue,
        minHeight: 16,
        maxHeight,
        enabled: autosizeHeight,
    });
    const handleChange = (event) => {
        if (preventLinebreaks) {
            // eslint-disable-next-line no-param-reassign
            event.target.value = removeLineBreaks(event.target.value);
        }
        if (onChange) {
            onChange(event);
        }
        else {
            setUncontrolledValue(event.target.value);
        }
    };
    const describedBy = ariaDescribedBy || (description && `${id}-description`);
    useDescriptionWarning({ ariaDescribedBy, description });
    const textAreaProps = Object.assign(Object.assign({}, restProps), { ref: textAreaRef, id,
        disabled, 'aria-describedby': describedBy, defaultValue,
        value,
        placeholder, onChange: handleChange, $resize: resize, $minHeight: minHeight, $maxHeight: maxHeight });
    return (<Container $stretch={stretch}>
      {label && (<Label htmlFor={id} $invalid={invalid} $labelSize={labelSize}>
          {label}
        </Label>)}
      <TextAreaContainer $invalid={invalid} $appearance={appearance}>
        {adornmentLeft && <Adornment>{adornmentLeft}</Adornment>}
        <StyledTextArea $variant={variant} {...textAreaProps}/>
        {adornmentRight && <Adornment>{adornmentRight}</Adornment>}
      </TextAreaContainer>
      {description && (<Description $invalid={invalid} id={describedBy} $disabled={disabled}>
          {description}
        </Description>)}
    </Container>);
});
